import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * TIDE v2 — Coastal Editorial
 *
 * Deep teal + cream + coral. A wedding by the sea. v2 fixes:
 *   - Faster scrollTrigger triggers (start: "top 90%")
 *   - Content visible by default (only subtle entrance shifts)
 *   - Denser content per act (more visuals + copy + secondary details)
 *   - Multiple ambient animations that loop forever (no scroll dep)
 *
 *  ACT I  HERO — Multi-layer morphing waves, 4 corner satellite
 *         postcards floating + bobbing, title with coral underline.
 *  ACT II PINNED TIDE-RISE — 3 wave layers rise + content reveals.
 *  ACT III SHIP'S LOG GRID — 4 journal cards in 2-column grid,
 *         each with date stamp + photo + quote. Always visible.
 *  ACT IV COMPASS DATE — Rotating compass + animated needle +
 *         coordinates + secondary info (weather, sea state).
 *  ACT V  HORIZONTAL HARBOUR — 5 postcards on horizontal pinned scroll,
 *         each fully designed with stamp + caption + photo.
 *  ACT VI MESSAGE IN A BOTTLE form — Wave-edge top, magnetic tilt.
 */
export function TideLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const rippleRef = useRef<HTMLDivElement>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const monthName = d?.toLocaleString("tr-TR", { month: "long" }) ?? "—";
  const year = d?.getFullYear() ?? 2026;

  // Ripple cursor
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!rippleRef.current) return;
      rippleRef.current.style.setProperty("--mx", `${e.clientX}px`);
      rippleRef.current.style.setProperty("--my", `${e.clientY}px`);
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  // Magnetic tilt
  useEffect(() => {
    const card = tiltRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.5, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── HERO: Morphing waves (ambient, runs forever) ────────────────
      const waveStates = [
        "M 0 50 Q 250 0 500 50 T 1000 50 L 1000 100 L 0 100 Z",
        "M 0 50 Q 250 100 500 50 T 1000 50 L 1000 100 L 0 100 Z",
        "M 0 50 Q 250 20 500 80 T 1000 50 L 1000 100 L 0 100 Z",
        "M 0 50 Q 250 80 500 20 T 1000 50 L 1000 100 L 0 100 Z",
      ];
      [".td-wave-1", ".td-wave-2", ".td-wave-3", ".td-wave-4"].forEach((sel, i) => {
        const tl = gsap.timeline({ repeat: -1, defaults: { ease: "sine.inOut", duration: 3 + i * 0.7 } });
        waveStates.forEach((d) => tl.to(sel, { attr: { d } }));
        tl.to(sel, { attr: { d: waveStates[0] } });
      });

      // Hero entrance
      const titleSplit = new SplitText(".td-hero-title", { type: "chars" });
      gsap.from(titleSplit.chars, { y: 60, opacity: 0, stagger: 0.05, duration: 1.3, ease: "expo.out" });
      gsap.from(".td-hero-meta", { y: 20, opacity: 0, stagger: 0.08, duration: 0.9, delay: 0.4 });
      gsap.from(".td-marquee-t", { x: "100%", duration: 1.6, ease: "expo.out" });
      gsap.from(".td-marquee-b", { x: "-100%", duration: 1.6, ease: "expo.out" });

      // 4 hero satellite postcards drift in from corners
      gsap.from(".td-sat-tl", { x: -100, y: -50, opacity: 0, rotate: -8, duration: 1.4, delay: 0.3, ease: "expo.out" });
      gsap.from(".td-sat-tr", { x: 100, y: -50, opacity: 0, rotate: 8, duration: 1.4, delay: 0.4, ease: "expo.out" });
      gsap.from(".td-sat-bl", { x: -100, y: 50, opacity: 0, rotate: 8, duration: 1.4, delay: 0.5, ease: "expo.out" });
      gsap.from(".td-sat-br", { x: 100, y: 50, opacity: 0, rotate: -8, duration: 1.4, delay: 0.6, ease: "expo.out" });

      // Continuous gentle bob on satellite cards
      gsap.to(".td-sat", { y: "+=8", duration: 3, ease: "sine.inOut", repeat: -1, yoyo: true, stagger: 0.3 });

      // ── ACT II: PINNED TIDE-RISE ────────────────────────────────────
      const tideSection = document.querySelector<HTMLElement>(".td-tide-section");
      if (tideSection) {
        ScrollTrigger.create({
          trigger: tideSection,
          start: "top top",
          end: "+=300%",
          pin: true,
          scrub: 1,
          onUpdate(self) {
            const w1 = document.querySelector<HTMLElement>(".td-tide-wave-1");
            const w2 = document.querySelector<HTMLElement>(".td-tide-wave-2");
            const w3 = document.querySelector<HTMLElement>(".td-tide-wave-3");
            if (w1) w1.style.transform = `translateY(${100 - self.progress * 120}%)`;
            if (w2) w2.style.transform = `translateY(${120 - self.progress * 150}%)`;
            if (w3) w3.style.transform = `translateY(${140 - self.progress * 180}%)`;
          },
        });

        // Caption visible from start, fades stronger mid-scroll
        gsap.fromTo(".td-tide-caption", { opacity: 0.3 }, {
          opacity: 1,
          ease: "none",
          scrollTrigger: { trigger: tideSection, start: "top top", end: "+=150%", scrub: 1 },
        });

        // Continue morphing tide waves
        const tideStates = [
          "M 0 100 Q 250 50 500 100 T 1000 100 L 1000 200 L 0 200 Z",
          "M 0 100 Q 250 150 500 100 T 1000 100 L 1000 200 L 0 200 Z",
        ];
        [".td-tide-wave-1 path", ".td-tide-wave-2 path", ".td-tide-wave-3 path"].forEach((sel, i) => {
          gsap.to(sel, { attr: { d: tideStates[1] }, duration: 2 + i * 0.5, ease: "sine.inOut", repeat: -1, yoyo: true });
        });
      }

      // ── ACT III: SHIP'S LOG (entrance only, content stays visible) ──
      gsap.from(".td-log-card", {
        y: 40, opacity: 0, stagger: 0.12, duration: 0.9, ease: "expo.out",
        scrollTrigger: { trigger: ".td-log-section", start: "top 90%", once: true },
      });
      gsap.from(".td-log-title", { y: 20, opacity: 0, duration: 0.9, ease: "expo.out",
        scrollTrigger: { trigger: ".td-log-section", start: "top 90%", once: true } });

      // ── ACT IV: COMPASS ─────────────────────────────────────────────
      gsap.from(".td-compass", { scale: 0.9, opacity: 0, duration: 1.2, ease: "expo.out",
        scrollTrigger: { trigger: ".td-date-section", start: "top 85%", once: true } });
      // Needle dramatic swing-in
      gsap.fromTo(".td-compass-needle", { rotate: -270 }, {
        rotate: 0, duration: 1.8, ease: "back.out(1.4)",
        scrollTrigger: { trigger: ".td-date-section", start: "top 85%", once: true },
      });
      gsap.from(".td-date-meta", { y: 20, opacity: 0, stagger: 0.1, duration: 0.9, ease: "power3.out",
        scrollTrigger: { trigger: ".td-date-section", start: "top 85%", once: true } });

      // Compass continuous slow rotate (decorative outer rings)
      gsap.to(".td-compass-ring", { rotate: 360, duration: 60, ease: "linear", repeat: -1, transformOrigin: "50% 50%" });

      // ── ACT V: HORIZONTAL HARBOUR ───────────────────────────────────
      const harb = document.querySelector<HTMLElement>(".td-harbour-section");
      const harbInner = document.querySelector<HTMLElement>(".td-harbour-inner");
      if (harb && harbInner) {
        const distance = harbInner.scrollWidth - window.innerWidth;
        gsap.to(harbInner, {
          x: -distance,
          ease: "none",
          scrollTrigger: { trigger: harb, start: "top top", end: `+=${distance}px`, pin: true, scrub: 1 },
        });
        gsap.to(".td-buoy", {
          y: (i) => (i % 2 === 0 ? "-10" : "10"),
          duration: 2.6,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          stagger: 0.3,
        });
      }

      // Form
      gsap.from(".td-form-card", { opacity: 0, y: 50, duration: 1.2, ease: "expo.out",
        scrollTrigger: { trigger: ".td-form-card", start: "top 90%", once: true } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  // Log entries
  const logEntries = [
    { date: "Primavera · 2024", title: "Il primo incontro", quote: "Beş saat geçti, fark etmedik. Yağmur başladı, hâlâ konuşuyorduk.", coord: "41° 03' N · İSTANBUL", img: galleryImages[0] },
    { date: "Estate · 2024", title: "I tre porti", quote: "Üç şehir gezdik. Her durakta yeni bir yön bulduk.", coord: "41° 53' N · ROMA", img: galleryImages[1] },
    { date: "Maggio · 2025", title: "La promessa", quote: "Plajda, yıldızlar altında. Bir tek söz verildi. Pusula değişti.", coord: "38° 19' N · ÇEŞME", img: galleryImages[3] },
    { date: `${monthName} · ${year}`, title: "L'ultimo porto", quote: "Final liman. Sizi düğünümüze davet ediyoruz — denizin kıyısında.", coord: `${day}° · ${(location ?? "").split(",")[0].toUpperCase()}`, img: galleryImages[4] },
  ];

  const buoys = [
    { caption: "L'incontro", year: "2024", quote: "Bir bahar akşamı" },
    { caption: "Roma", year: "2024", quote: "Trastevere'de" },
    { caption: "Paris", year: "2024", quote: "Rive Gauche'da" },
    { caption: "La promessa", year: "2025", quote: "Yıldızların altında" },
    { caption: "Oggi", year: year.toString(), quote: "Sizinle birlikte" },
  ];

  return (
    <main className="relative overflow-x-hidden" style={{ background: "#082c33", color: "#f4f0e3", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');
        @keyframes tdMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes tdMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>

      {/* Background */}
      <div className="fixed inset-0 -z-30 pointer-events-none" style={{
        background: "radial-gradient(ellipse at top, #1a5868 0%, #082c33 55%, #03161c 100%)",
      }} />

      {/* Cursor ripple */}
      <div
        ref={rippleRef}
        className="fixed inset-0 pointer-events-none z-0 hidden md:block"
        style={{
          background: "radial-gradient(circle 350px at var(--mx, 50%) var(--my, 50%), rgba(244, 178, 134, 0.12), transparent 70%)",
          mixBlendMode: "screen" as const,
        }}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 px-8 py-4 flex justify-between items-center text-[10px] uppercase tracking-[0.5em]"
        style={{ background: "rgba(8, 44, 51, 0.7)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(244, 178, 134, 0.25)" }}>
        <span style={{ color: "#f4b286" }}>Tide ⟂ {year}</span>
        <span className="italic normal-case text-base tracking-normal text-[#f4f0e3]/70" style={{ fontFamily: "'Cormorant Garamond', serif" }}>~ Per mare ~</span>
        <span style={{ color: "#f4b286" }}>{monthShort} {day}</span>
      </header>

      {/* ═══ ACT I: HERO ═══════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
        {/* Top marquee */}
        <div className="td-marquee-t relative h-10 border-b border-[#f4b286]/20 overflow-hidden flex items-center mt-12">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#f4b286", animation: "tdMQ_L 36s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                ⟂ {title.toUpperCase()} ⟂ A WEDDING BY THE SEA ⟂ {monthShort} {day} {year} ⟂ HIGH TIDE 18:00 ⟂ AZIMUTH 270° ⟂
              </span>
            ))}
          </div>
        </div>

        {/* 4 satellite postcards floating in corners */}
        <div className="td-sat td-sat-tl absolute top-24 left-8 hidden md:block" style={{ width: 110, transform: "rotate(-6deg)" }}>
          <div className="bg-[#f4f0e3] p-2 pb-6 shadow-2xl">
            <div className="w-full h-32 overflow-hidden">
              <img src={galleryImages[0]} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.15) brightness(0.9)" }} />
            </div>
            <p className="text-center text-[10px] italic mt-1 text-[#082c33]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>I · 2024</p>
          </div>
        </div>
        <div className="td-sat td-sat-tr absolute top-32 right-8 hidden md:block" style={{ width: 110, transform: "rotate(6deg)" }}>
          <div className="bg-[#f4f0e3] p-2 pb-6 shadow-2xl">
            <div className="w-full h-32 overflow-hidden">
              <img src={galleryImages[1]} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.15) brightness(0.9)" }} />
            </div>
            <p className="text-center text-[10px] italic mt-1 text-[#082c33]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>II · 2024</p>
          </div>
        </div>
        <div className="td-sat td-sat-bl absolute bottom-40 left-12 hidden md:block" style={{ width: 110, transform: "rotate(4deg)" }}>
          <div className="bg-[#f4f0e3] p-2 pb-6 shadow-2xl">
            <div className="w-full h-32 overflow-hidden">
              <img src={galleryImages[2]} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.15) brightness(0.9)" }} />
            </div>
            <p className="text-center text-[10px] italic mt-1 text-[#082c33]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>III · 2025</p>
          </div>
        </div>
        <div className="td-sat td-sat-br absolute bottom-44 right-12 hidden md:block" style={{ width: 110, transform: "rotate(-4deg)" }}>
          <div className="bg-[#f4f0e3] p-2 pb-6 shadow-2xl">
            <div className="w-full h-32 overflow-hidden">
              <img src={galleryImages[3]} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.15) brightness(0.9)" }} />
            </div>
            <p className="text-center text-[10px] italic mt-1 text-[#082c33]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>IV · {year}</p>
          </div>
        </div>

        {/* Center title */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
          <p className="td-hero-meta text-[10px] uppercase tracking-[0.6em] mb-6" style={{ color: "#f4b286" }}>— Marigatto No. {year} —</p>

          <h1 className="td-hero-title font-light italic leading-[0.85] tracking-[-0.04em] text-center"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "clamp(4rem, 13vw, 11rem)",
              color: "#f4f0e3",
              textShadow: "0 0 60px rgba(244, 178, 134, 0.18)",
            }}>
            {title}
          </h1>

          {/* Coral underline */}
          <div className="td-hero-meta mt-10 w-32 h-px" style={{ background: "linear-gradient(90deg, transparent, #f4b286 50%, transparent)" }} />

          <p className="td-hero-meta mt-8 italic text-2xl" style={{ color: "#f4b286", fontFamily: "'Cormorant Garamond', serif" }}>
            ~ con vista sul mare ~
          </p>
          <p className="td-hero-meta mt-3 text-[10px] uppercase tracking-[0.5em] text-[#f4f0e3]/60">
            {d?.toLocaleString("tr-TR", { weekday: "long" })} · {monthShort} {day} · {year} · {d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}
          </p>
        </div>

        {/* Stacked morphing waves */}
        <div className="absolute bottom-0 left-0 right-0 h-[40vh] pointer-events-none">
          <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path className="td-wave-4" d="M 0 50 Q 250 0 500 50 T 1000 50 L 1000 200 L 0 200 Z" fill="rgba(244, 178, 134, 0.08)" />
          </svg>
          <svg viewBox="0 0 1000 150" preserveAspectRatio="none" className="absolute bottom-0 w-full" style={{ height: "30vh" }}>
            <path className="td-wave-3" d="M 0 50 Q 250 20 500 80 T 1000 50 L 1000 150 L 0 150 Z" fill="rgba(244, 178, 134, 0.12)" />
          </svg>
          <svg viewBox="0 0 1000 120" preserveAspectRatio="none" className="absolute bottom-0 w-full" style={{ height: "22vh" }}>
            <path className="td-wave-2" d="M 0 50 Q 250 100 500 50 T 1000 50 L 1000 120 L 0 120 Z" fill="rgba(8, 44, 51, 0.6)" />
          </svg>
          <svg viewBox="0 0 1000 100" preserveAspectRatio="none" className="absolute bottom-0 w-full" style={{ height: "16vh" }}>
            <path className="td-wave-1" d="M 0 50 Q 250 0 500 50 T 1000 50 L 1000 100 L 0 100 Z" fill="#082c33" />
          </svg>
        </div>

        {/* Bottom marquee */}
        <div className="td-marquee-b relative h-10 border-t border-[#f4b286]/20 overflow-hidden flex items-center z-10">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase text-[#f4f0e3]/60" style={{ animation: "tdMQ_R 40s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">◇ LAT 36.42° N ◇ LON 28.13° E ◇ {(location ?? "").toUpperCase()} ◇ WEATHER: CLEAR ◇ SEA: CALM ◇</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: TIDE RISE ═════════════════════════════════════════ */}
      <section className="td-tide-section relative h-screen overflow-hidden">
        <div className="absolute top-12 left-12 z-30 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#f4b286" }}>II · ALTA MAREA</div>
        <div className="absolute top-12 right-12 z-30 text-[10px] uppercase tracking-[0.5em] text-[#f4f0e3]/40">~ rising ~</div>

        <div className="absolute inset-0">
          <img src={galleryImages[2]} alt="" className="w-full h-full object-cover" style={{ filter: "saturate(0.85) contrast(1.05) brightness(0.55)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8, 44, 51, 0.5) 0%, transparent 30%)" }} />
        </div>

        <div className="td-tide-wave-3 absolute left-0 right-0 h-[120%] bottom-0 pointer-events-none" style={{ transform: "translateY(140%)" }}>
          <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="absolute top-0 w-full" style={{ height: "30%" }}>
            <path d="M 0 100 Q 250 50 500 100 T 1000 100 L 1000 200 L 0 200 Z" fill="rgba(26, 88, 104, 0.5)" />
          </svg>
          <div className="absolute top-[30%] bottom-0 left-0 right-0" style={{ background: "rgba(26, 88, 104, 0.5)" }} />
        </div>
        <div className="td-tide-wave-2 absolute left-0 right-0 h-[120%] bottom-0 pointer-events-none" style={{ transform: "translateY(120%)" }}>
          <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="absolute top-0 w-full" style={{ height: "25%" }}>
            <path d="M 0 100 Q 250 150 500 100 T 1000 100 L 1000 200 L 0 200 Z" fill="rgba(8, 44, 51, 0.7)" />
          </svg>
          <div className="absolute top-[25%] bottom-0 left-0 right-0" style={{ background: "rgba(8, 44, 51, 0.7)" }} />
        </div>
        <div className="td-tide-wave-1 absolute left-0 right-0 h-[120%] bottom-0 pointer-events-none" style={{ transform: "translateY(100%)" }}>
          <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="absolute top-0 w-full" style={{ height: "20%" }}>
            <path d="M 0 100 Q 250 50 500 100 T 1000 100 L 1000 200 L 0 200 Z" fill="#082c33" />
          </svg>
          <div className="absolute top-[20%] bottom-0 left-0 right-0" style={{ background: "#082c33" }} />
        </div>

        <div className="td-tide-caption absolute inset-0 flex items-center justify-center px-6 pointer-events-none z-10">
          <div className="text-center max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.6em] mb-6" style={{ color: "#f4b286" }}>— Alta Marea —</p>
            <p className="font-light italic leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
              "Bir gel-git gibi —<br />kaçınılmaz, sonsuz."
            </p>
            <p className="italic text-lg sm:text-xl text-[#f4f0e3]/70 max-w-xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Aşk, bizi denizin huyu gibi kuşatır — bazen yumuşak, bazen güçlü, her zaman kaçınılmaz.
            </p>
            <div className="flex items-center justify-center gap-6 mt-8 text-[10px] uppercase tracking-[0.5em] text-[#f4f0e3]/50">
              <span>LATITUDE 36.42°N</span>
              <span style={{ color: "#f4b286" }}>◆</span>
              <span>HIGH TIDE 18:00</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ACT III: SHIP'S LOG GRID ══════════════════════════════════ */}
      <section className="td-log-section relative py-32 px-6 z-10">
        <div className="max-w-6xl mx-auto">
          <p className="td-log-title text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#f4b286" }}>III · DIARIO DI BORDO</p>
          <h2 className="td-log-title text-center font-light italic leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            Captain's Log
          </h2>
          <p className="td-log-title text-center italic text-lg text-[#f4f0e3]/60 mb-16 max-w-xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Four entries · two years at sea · one heart bound for harbour.
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {logEntries.map((e, i) => (
              <div key={i} className="td-log-card relative grid grid-cols-12 gap-4 p-6 sm:p-8" style={{
                background: "linear-gradient(135deg, rgba(244, 178, 134, 0.06) 0%, rgba(244, 178, 134, 0.02) 100%)",
                border: "1px solid rgba(244, 178, 134, 0.2)",
                backdropFilter: "blur(8px)",
              }}>
                <div className="col-span-4 aspect-square overflow-hidden" style={{ border: "1px solid rgba(244, 178, 134, 0.3)" }}>
                  <img src={e.img} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.18) saturate(0.95)" }} />
                </div>
                <div className="col-span-8 flex flex-col justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.4em] mb-2" style={{ color: "#f4b286" }}>{e.date}</p>
                    <h3 className="italic font-light leading-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)" }}>
                      {e.title}
                    </h3>
                    <p className="italic text-sm sm:text-base leading-relaxed text-[#f4f0e3]/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      "{e.quote}"
                    </p>
                  </div>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.4em] font-mono" style={{ color: "#f4b286" }}>~ {e.coord}</p>
                </div>
                {/* Corner stamp */}
                <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.3em] font-mono" style={{ color: "rgba(244, 178, 134, 0.5)" }}>
                  N° {String(i + 1).padStart(3, "0")}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT IV: COMPASS DATE ══════════════════════════════════════ */}
      <section className="td-date-section relative py-32 px-6 z-10">
        <div className="max-w-5xl mx-auto">
          <p className="td-date-meta text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#f4b286" }}>IV · BUSSOLA</p>
          <h2 className="td-date-meta text-center font-light italic leading-tight mb-16" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            Set your bearings.
          </h2>

          <div className="grid lg:grid-cols-3 gap-12 items-center">
            {/* Left: weather info */}
            <div className="td-date-meta space-y-5 text-right">
              {[
                { k: "Date", v: `${day} ${monthShort} ${year}` },
                { k: "Weekday", v: d?.toLocaleString("en-US", { weekday: "long" }) ?? "—" },
                { k: "High Tide", v: d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) ?? "—" },
                { k: "Wind", v: "Light · 4 kn" },
              ].map((r, i) => (
                <div key={i} className="border-r-2 pr-4" style={{ borderColor: "#f4b286" }}>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#f4f0e3]/40">{r.k}</p>
                  <p className="italic text-xl mt-1" style={{ color: "#f4f0e3", fontFamily: "'Cormorant Garamond', serif" }}>{r.v}</p>
                </div>
              ))}
            </div>

            {/* Center: Compass */}
            <div className="td-compass relative mx-auto" style={{ width: 320, height: 320 }}>
              <svg viewBox="0 0 320 320" className="w-full h-full">
                <g className="td-compass-ring">
                  <circle cx="160" cy="160" r="155" fill="none" stroke="#f4b286" strokeWidth="1" />
                  <circle cx="160" cy="160" r="148" fill="none" stroke="#f4b286" strokeWidth="0.5" opacity="0.5" />
                  {Array.from({ length: 72 }).map((_, i) => {
                    const angle = (i / 72) * 360 - 90;
                    const rad = (angle * Math.PI) / 180;
                    const isMajor = i % 6 === 0;
                    const r1 = isMajor ? 130 : 138;
                    const r2 = 148;
                    return <line key={i} x1={160 + Math.cos(rad) * r1} y1={160 + Math.sin(rad) * r1} x2={160 + Math.cos(rad) * r2} y2={160 + Math.sin(rad) * r2} stroke="#f4b286" strokeWidth={isMajor ? 1.2 : 0.5} opacity={isMajor ? 1 : 0.5} />;
                  })}
                </g>

                {/* Cardinal letters — fixed (don't rotate with the ring) */}
                <text x="160" y="40" textAnchor="middle" style={{ fill: "#f4b286", fontSize: 18, fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>N</text>
                <text x="280" y="167" textAnchor="middle" style={{ fill: "#f4b286", fontSize: 18, fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>E</text>
                <text x="160" y="295" textAnchor="middle" style={{ fill: "#f4b286", fontSize: 18, fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>S</text>
                <text x="40" y="167" textAnchor="middle" style={{ fill: "#f4b286", fontSize: 18, fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>W</text>

                {/* Decorative star */}
                <g transform="translate(160 160)" opacity="0.4">
                  <polygon points="0,-100 8,-8 100,0 8,8 0,100 -8,8 -100,0 -8,-8" fill="#f4b286" />
                  <polygon points="0,-60 4,-4 60,0 4,4 0,60 -4,4 -60,0 -4,-4" fill="#082c33" />
                </g>

                {/* Needle */}
                <g className="td-compass-needle" style={{ transformOrigin: "160px 160px" }}>
                  <polygon points="160,60 168,160 160,180 152,160" fill="#d4423a" />
                  <polygon points="160,260 168,160 160,140 152,160" fill="#f4f0e3" />
                  <circle cx="160" cy="160" r="6" fill="#f4b286" stroke="#082c33" strokeWidth="1.5" />
                </g>
              </svg>

              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ marginTop: 100 }}>
                <p className="font-light tabular-nums leading-none" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 56, color: "#f4f0e3" }}>{day}</p>
                <p className="italic text-sm" style={{ color: "#f4b286" }}>{monthShort}.{year}</p>
              </div>
            </div>

            {/* Right: coordinates */}
            <div className="td-date-meta space-y-5">
              {[
                { k: "Latitude", v: "36° 25' 12\" N" },
                { k: "Longitude", v: "28° 13' 48\" E" },
                { k: "Bearing", v: "270° West" },
                { k: "Destination", v: location?.split(",")[0] ?? "—" },
              ].map((r, i) => (
                <div key={i} className="border-l-2 pl-4" style={{ borderColor: "#f4b286" }}>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#f4f0e3]/40">{r.k}</p>
                  <p className="italic text-xl mt-1" style={{ color: "#f4f0e3", fontFamily: "'Cormorant Garamond', serif" }}>{r.v}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="td-date-meta text-center mt-16 italic text-lg text-[#f4f0e3]/70" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            ~ all roads lead to {(location ?? "").split(",")[0]} ~
          </p>
        </div>
      </section>

      {/* ═══ ACT V: HORIZONTAL HARBOUR ═════════════════════════════════ */}
      <section className="td-harbour-section relative h-screen overflow-hidden z-10">
        <div className="absolute top-12 left-12 z-30 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#f4b286" }}>V · IL PORTO</div>
        <div className="absolute top-12 right-12 z-30 text-[10px] uppercase tracking-[0.5em] text-[#f4f0e3]/40">5 BUOYS · SCROLL →</div>

        {/* Background wave decoration */}
        <svg viewBox="0 0 1000 100" preserveAspectRatio="none" className="absolute bottom-32 left-0 w-full opacity-30 pointer-events-none" style={{ height: 60 }}>
          <path d="M 0 50 Q 250 20 500 50 T 1000 50 L 1000 100 L 0 100 Z" fill="#f4b286" />
        </svg>

        <div className="td-harbour-inner absolute top-0 left-0 h-full flex items-center" style={{ width: "max-content" }}>
          {/* Intro */}
          <div className="flex-shrink-0 w-screen h-full flex items-center justify-center px-12">
            <div className="text-center max-w-md">
              <p className="text-[10px] uppercase tracking-[0.6em] mb-4" style={{ color: "#f4b286" }}>— I Ricordi —</p>
              <p className="italic font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                Anılarımız<br />limanda demirli.
              </p>
              <p className="mt-6 italic text-lg text-[#f4f0e3]/60" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Cinque cartoline · five postcards · beş hatıra.
              </p>
            </div>
          </div>

          {/* 5 photo buoys */}
          {buoys.map((b, i) => (
            <div key={i} className="td-buoy flex-shrink-0 h-full flex items-center justify-center px-10" style={{ width: "65vw" }}>
              <div className="relative">
                <div className="bg-[#f4f0e3] p-3 pb-14 sm:pb-16" style={{ boxShadow: "0 30px 60px rgba(0,0,0,0.4)" }}>
                  <div className="relative w-72 h-96 sm:w-80 sm:h-[28rem] overflow-hidden">
                    <img src={galleryImages[i]} alt="" className="w-full h-full object-cover" style={{ filter: "saturate(0.9) contrast(1.05)" }} />
                    {/* Postage stamp */}
                    <div className="absolute top-2 right-2 w-12 h-14 flex flex-col items-center justify-center text-center" style={{ background: "#f4f0e3", border: "1px dashed #f4b286" }}>
                      <span className="text-[7px] uppercase tracking-widest" style={{ color: "#f4b286" }}>POSTA</span>
                      <span className="text-[6px] mt-0.5" style={{ color: "#082c33" }}>{b.year}</span>
                    </div>
                    {/* Number tag */}
                    <div className="absolute bottom-2 left-2 px-2 py-1" style={{ background: "rgba(8, 44, 51, 0.8)" }}>
                      <span className="text-[9px] uppercase tracking-[0.3em]" style={{ color: "#f4b286" }}>N° {String(i + 1).padStart(2, "0")}</span>
                    </div>
                  </div>
                  <p className="text-center mt-3 italic text-2xl text-[#082c33]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{b.caption}</p>
                  <p className="text-center text-[10px] italic text-[#082c33]/60 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>~ {b.quote} ~</p>
                  <p className="text-center text-[9px] uppercase tracking-[0.4em] text-[#082c33]/50 mt-2">{b.year}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Outro */}
          <div className="flex-shrink-0 w-screen h-full flex items-center justify-center px-12">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#f4b286" }}>~ Fine del viaggio ~</p>
              <p className="italic text-2xl text-[#f4f0e3]/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                The harbour awaits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ACT VI: MESSAGE IN A BOTTLE ═══════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32 z-10" style={{ perspective: 1200 }}>
        <div
          ref={tiltRef}
          className="td-form-card w-full max-w-md p-12 pt-16 relative transition-transform duration-300 ease-out text-center"
          style={{
            background: "linear-gradient(180deg, #0c4856 0%, #082c33 100%)",
            border: "1px solid rgba(244, 178, 134, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(244, 178, 134, 0.2), 0 40px 100px rgba(0, 0, 0, 0.6)",
            transformStyle: "preserve-3d",
          }}
        >
          <svg viewBox="0 0 100 20" preserveAspectRatio="none" className="absolute top-0 left-0 right-0 w-full pointer-events-none" style={{ height: 30, transform: "translateY(-50%)" }}>
            <path d="M 0 10 Q 12 0 25 10 T 50 10 T 75 10 T 100 10" stroke="#f4b286" strokeWidth="1.5" fill="none" />
            <path d="M 0 10 Q 12 0 25 10 T 50 10 T 75 10 T 100 10 L 100 20 L 0 20 Z" fill="#0c4856" />
          </svg>

          <div className="absolute top-3 left-3 w-5 h-5 border-t border-l" style={{ borderColor: "#f4b286" }} />
          <div className="absolute top-3 right-3 w-5 h-5 border-t border-r" style={{ borderColor: "#f4b286" }} />
          <div className="absolute bottom-3 left-3 w-5 h-5 border-b border-l" style={{ borderColor: "#f4b286" }} />
          <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r" style={{ borderColor: "#f4b286" }} />

          <p className="text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#f4b286" }}>VI · MESSAGGIO</p>
          <h3 className="font-light italic mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", color: "#f4f0e3" }}>
            Send a reply
          </h3>
          <p className="italic mb-10 text-[#f4b286]/80" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16 }}>
            ~ message in a bottle ~
          </p>
          <div className="mx-auto w-12 h-px mb-10" style={{ background: "#f4b286" }} />

          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
